interface ICreateLocatorsArgs<P> {
  name?: string;
  componentLocators: P[];
}

const kebab = (key: string) =>
  key.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();

export const dataAttr = (attr: string) => `data-${kebab(attr)}`;
export const KEY = dataAttr('testid');

type Suffix = string | number;
type Attribute = string | number | boolean | undefined;
type Attributes = Record<string, Attribute>;

export const toLocator = ({
  map,
  locator,
  suffix,
}: {
  map: Map<string, string>;
  locator: string;
  suffix?: Suffix;
}) => [map.get(locator), suffix].filter(Boolean).join('-');

export function createLocators<P extends string>({
  name: prefix,
  componentLocators,
}: ICreateLocatorsArgs<P>) {
  const testIdAttribute = KEY;
  const map = componentLocators.reduce((memo, locator) => {
    memo.set(locator, [prefix, locator].filter(Boolean).join('-'));
    return memo;
  }, new Map<string, string>());

  const createGenerateQueryString = (locator: string) => {
    function generateQueryString(): string;
    function generateQueryString(
      suffix: Suffix,
      additional?: Attributes,
    ): string;
    function generateQueryString(additional?: Attributes): string;
    function generateQueryString(...args: any[]) {
      const suffix = typeof args[0] !== 'object' ? args[0] : undefined;

      const additional =
        typeof args[1] !== 'undefined'
          ? args[1]
          : typeof args[0] === 'object'
          ? args[0]
          : {};

      const base = `[${testIdAttribute}=${toLocator({
        map,
        locator,
        suffix,
      })}]`;

      return [
        base,
        ...Object.keys(additional).map(key => {
          return `[${dataAttr(key)}=${additional[key]}]`;
        }),
      ].join('');
    }

    return generateQueryString;
  };

  const createGenerateLocator = (locator: string) => {
    function generateLocator(): Record<string, Attribute>;
    function generateLocator(
      suffix: Suffix,
      additional?: Attributes,
    ): Record<string, Attribute>;
    function generateLocator(
      additional?: Attributes,
    ): Record<string, Attribute>;
    function generateLocator(...args: any[]) {
      const suffix = typeof args[0] !== 'object' ? args[0] : undefined;

      const additional =
        typeof args[1] !== 'undefined'
          ? args[1]
          : typeof args[0] === 'object'
          ? args[0]
          : {};

      const locators = Object.keys(additional).reduce<Attributes>(
        (memo, key) => {
          memo[dataAttr(key)] = additional[key];
          return memo;
        },
        {},
      );

      return {
        [testIdAttribute]: toLocator({ map, locator, suffix }),
        ...locators,
      };
    }

    return generateLocator;
  };

  return () => {
    /* tslint:disable no-object-literal-type-assertion */
    const locators = {} as Record<P, ReturnType<typeof createGenerateLocator>>;
    const queryString = {} as Record<
      P,
      ReturnType<typeof createGenerateQueryString>
    >;
    const testIds = {} as Record<P, (s?: Suffix) => string>;
    /* tslint-enable no-object-literal-type-assertionno-object-literal-type-assertion */

    componentLocators.forEach(locator => {
      locators[locator] = createGenerateLocator(locator);
      queryString[locator] = createGenerateQueryString(locator);

      testIds[locator] = (suffix?: Suffix) =>
        toLocator({ map, locator, suffix });
    });

    return {
      locators,
      testIds,
      queryString,
    };
  };
}
